import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Activity, TrendingDown, TrendingUp, BarChart3, Upload, RefreshCw,
  AlertTriangle, CheckCircle, Clock, Zap, Target, ArrowUpDown, ChevronDown, ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DurationGapResult {
  assetDuration: number;
  liabilityDuration: number;
  durationGap: number;
  assetDV01: number;
  liabilityDV01: number;
  dv01Mismatch: number;
  totalAssetMV: number;
  totalLiabilityPV: number;
  surplus: number;
}

interface KRDMismatchEntry {
  tenor: number;
  assetKRD: number;
  liabilityKRD: number;
  mismatch: number;
  dv01Mismatch: number;
}

interface RateShockResult {
  shockBps: number;
  totalImpact: number;
  impactPercent: number;
  impactByTenor: KRDMismatchEntry[];
}

interface CashFlowPeriod {
  year: number;
  quarter: number;
  assetInflow: number;
  liabilityOutflow: number;
  netCashFlow: number;
  cumulativeBalance: number;
}

interface CashFlowProjectionResult {
  portfolioId: number;
  scenario: string;
  reinvestmentRate: number;
  periods: CashFlowPeriod[];
  deficitPeriodCount: number;
  maxDeficit: number;
  terminalBalance: number;
}

interface ALMSummary {
  durationGap: DurationGapResult;
  krdMismatches: KRDMismatchEntry[];
  lastActuarialUpdate: string | null;
  staleness: string;
}

interface Portfolio { id: number; portfolioName: string; }

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const Container = styled.div`min-height: calc(100vh - 64px); background: transparent;`;
const TabsContainer = styled.div`background: linear-gradient(to bottom, rgba(15, 23, 42, 0.5) 0%, transparent 100%); padding: 1.5rem 2rem 0;`;
const TabsWrapper = styled.div`display: flex; gap: 4px; max-width: 1800px; margin: 0 auto; border-bottom: 1px solid rgba(255, 255, 255, 0.05);`;
const ContentContainer = styled.div`max-width: 1800px; margin: 0 auto; padding: 2rem;`;
const GlassCard = styled.div`background: rgba(30, 41, 59, 0.5); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; overflow: hidden;`;

const ALMTab: React.FC<{ icon: React.ReactNode; label: string; active: boolean; onClick: () => void }> = ({ icon, label, active, onClick }) => (
  <button onClick={onClick} className={cn("group flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all duration-200 relative", active ? "text-white" : "text-slate-400 hover:text-slate-200")}>
    <span className={cn("transition-transform duration-200 group-hover:scale-110", active ? "text-cyan-400" : "text-slate-500 group-hover:text-slate-300")}>{icon}</span>
    {label}
    {active && <motion.div layoutId="alm-nav-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]" />}
  </button>
);

const fmtCurrency = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
const fmtPct = (v: number, digits = 2) => `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;
const fmtYears = (v: number) => `${v.toFixed(3)} yrs`;

// ---------------------------------------------------------------------------
// Tab 1: ALM Summary & Duration Gap
// ---------------------------------------------------------------------------

const ALMSummaryTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [summary, setSummary] = useState<ALMSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    axios.get(`/api/insurance/alm/summary/${portfolioId}`)
      .then(res => setSummary(res.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  if (!portfolioId) return <GlassCard className="p-12 text-center text-slate-500">Select a portfolio above</GlassCard>;
  if (loading) return <GlassCard className="p-12 text-center text-slate-500">Calculating ALM metrics...</GlassCard>;
  if (!summary) return <GlassCard className="p-12 text-center text-slate-500">No ALM data available. Upload liability cash flows first.</GlassCard>;

  const dg = summary.durationGap;
  const gapColor = Math.abs(dg.durationGap) <= 0.5 ? 'text-emerald-400' : Math.abs(dg.durationGap) <= 1.0 ? 'text-amber-400' : 'text-red-400';
  const gapBorder = Math.abs(dg.durationGap) <= 0.5 ? 'border-l-emerald-500' : Math.abs(dg.durationGap) <= 1.0 ? 'border-l-amber-500' : 'border-l-red-500';

  return (
    <div className="space-y-6">
      {/* Staleness indicator */}
      <div className="flex items-center gap-3 text-sm">
        <Clock className="w-4 h-4 text-slate-500" />
        <span className="text-slate-500">Last actuarial update:</span>
        <span className={cn("font-semibold", summary.staleness === 'CURRENT' ? 'text-emerald-400' : summary.staleness === 'STALE' ? 'text-amber-400' : 'text-red-400')}>
          {summary.lastActuarialUpdate ? new Date(summary.lastActuarialUpdate).toLocaleDateString() : 'Never'}
        </span>
        <span className={cn("px-2 py-0.5 text-[10px] font-bold rounded", summary.staleness === 'CURRENT' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400')}>
          {summary.staleness}
        </span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <GlassCard className={cn("p-5 border-l-4", gapBorder)}>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Duration Gap</div>
          <div className={cn("text-2xl font-bold", gapColor)}>{fmtYears(dg.durationGap)}</div>
          <div className="text-[10px] text-slate-500 mt-1">Target: ±0.5 years</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-blue-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Asset Duration</div>
          <div className="text-2xl font-bold text-white">{fmtYears(dg.assetDuration)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-violet-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Liability Duration</div>
          <div className="text-2xl font-bold text-white">{fmtYears(dg.liabilityDuration)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-cyan-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Surplus</div>
          <div className={cn("text-2xl font-bold", dg.surplus >= 0 ? "text-emerald-400" : "text-red-400")}>{fmtCurrency(dg.surplus)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-amber-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">DV01 Mismatch</div>
          <div className="text-2xl font-bold text-white">{fmtCurrency(Math.abs(dg.dv01Mismatch))}/bp</div>
        </GlassCard>
      </div>

      {/* KRD Mismatch Chart */}
      <GlassCard className="p-6">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Key Rate Duration Mismatch by Tenor</h3>
        <div className="space-y-2">
          {(summary.krdMismatches || []).map(krd => {
            const barWidth = Math.min(Math.abs(krd.mismatch) * 50, 100);
            const isOverweight = krd.mismatch > 0;
            return (
              <div key={krd.tenor} className="flex items-center gap-4">
                <span className="text-sm font-mono text-slate-400 w-12 text-right">{krd.tenor}yr</span>
                <div className="flex-1 flex items-center gap-2">
                  <div className="w-24 text-right">
                    {!isOverweight && (
                      <div className="h-5 rounded-l" style={{ width: `${barWidth}%`, marginLeft: `${100 - barWidth}%`, background: 'rgba(239, 68, 68, 0.4)' }} />
                    )}
                  </div>
                  <div className="w-px h-6 bg-slate-600" />
                  <div className="w-24">
                    {isOverweight && (
                      <div className="h-5 rounded-r" style={{ width: `${barWidth}%`, background: 'rgba(16, 185, 129, 0.4)' }} />
                    )}
                  </div>
                </div>
                <span className={cn("text-xs font-mono w-20 text-right", Math.abs(krd.mismatch) > 0.3 ? "text-red-400 font-bold" : "text-slate-400")}>
                  {krd.mismatch > 0 ? '+' : ''}{krd.mismatch.toFixed(3)}
                </span>
                <span className="text-[10px] text-slate-600 w-24 text-right">DV01: {fmtCurrency(Math.abs(krd.dv01Mismatch))}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[10px] text-slate-600">
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-400/40" /> Underweight (short)</span>
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-400/40" /> Overweight (long)</span>
          <span className="ml-auto">Tolerance: ±0.3 years per tenor</span>
        </div>
      </GlassCard>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 2: Rate Shock Simulator
// ---------------------------------------------------------------------------

const RateShockTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [shockBps, setShockBps] = useState(100);
  const [result, setResult] = useState<RateShockResult | null>(null);
  const [loading, setLoading] = useState(false);

  const runShock = async () => {
    if (!portfolioId) return;
    setLoading(true);
    try {
      const res = await axios.post(`/api/insurance/alm/rate-shock/${portfolioId}`, null, { params: { shockBps } });
      setResult(res.data);
    } catch { setResult(null); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <GlassCard className="p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-cyan-400" /> Rate Shock Simulator
        </h3>
        <div className="flex items-end gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Parallel Shift (bps)</label>
            <div className="flex gap-2">
              {[-200, -100, -50, 50, 100, 200, 300].map(bps => (
                <button key={bps} onClick={() => setShockBps(bps)}
                  className={cn("px-3 py-2 rounded-lg text-sm font-bold transition-all",
                    shockBps === bps ? "bg-cyan-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700")}>
                  {bps > 0 ? '+' : ''}{bps}
                </button>
              ))}
            </div>
          </div>
          <button onClick={runShock} disabled={loading || !portfolioId}
            className="px-6 py-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold rounded-lg flex items-center gap-2">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Run Shock
          </button>
        </div>
      </GlassCard>

      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <GlassCard className={cn("p-5 border-l-4", result.totalImpact < 0 ? "border-l-red-500" : "border-l-emerald-500")}>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Surplus Impact</div>
              <div className={cn("text-2xl font-bold", result.totalImpact < 0 ? "text-red-400" : "text-emerald-400")}>{fmtCurrency(result.totalImpact)}</div>
            </GlassCard>
            <GlassCard className="p-5 border-l-4 border-l-blue-500">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Impact %</div>
              <div className="text-2xl font-bold text-white">{fmtPct(result.impactPercent)}</div>
            </GlassCard>
            <GlassCard className="p-5 border-l-4 border-l-amber-500">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Shock Applied</div>
              <div className="text-2xl font-bold text-white">{result.shockBps > 0 ? '+' : ''}{result.shockBps} bps</div>
            </GlassCard>
          </div>

          <GlassCard className="p-6">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Impact by Tenor</h3>
            <div className="space-y-2">
              {(result.impactByTenor || []).map(entry => (
                <div key={entry.tenor} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-white/5">
                  <span className="text-sm font-mono text-slate-400">{entry.tenor}yr</span>
                  <span className={cn("text-sm font-bold", entry.dv01Mismatch < 0 ? "text-red-400" : "text-emerald-400")}>
                    {fmtCurrency(entry.dv01Mismatch * shockBps)}
                  </span>
                </div>
              ))}
            </div>
          </GlassCard>
        </motion.div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 3: Cash Flow Projection
// ---------------------------------------------------------------------------

const CashFlowProjectionTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [scenario, setScenario] = useState('BASE');
  const [reinvestRate, setReinvestRate] = useState(5.0);
  const [result, setResult] = useState<CashFlowProjectionResult | null>(null);
  const [loading, setLoading] = useState(false);

  const runProjection = async () => {
    if (!portfolioId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/insurance/alm/cash-flow-projection/${portfolioId}`, {
        params: { scenario, reinvestmentRate: reinvestRate / 100 }
      });
      setResult(res.data);
    } catch { setResult(null); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <GlassCard className="p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-400" /> Cash Flow Projection
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Scenario</label>
            <select value={scenario} onChange={e => setScenario(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-900 border border-white/10 rounded-lg text-white text-sm">
              <option value="BASE">Base Case</option>
              <option value="RATES_UP_300">Rates Up 300bps</option>
              <option value="RATES_DOWN_200">Rates Down 200bps</option>
              <option value="RATES_UP_GRADED">Graded Up 300bps</option>
              <option value="RATES_DOWN_GRADED">Graded Down 200bps</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Reinvestment Rate (%)</label>
            <input type="number" value={reinvestRate} step={0.25}
              onChange={e => setReinvestRate(parseFloat(e.target.value) || 5.0)}
              className="w-full px-4 py-2.5 bg-slate-900 border border-white/10 rounded-lg text-white text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={runProjection} disabled={loading || !portfolioId}
              className="w-full px-6 py-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold rounded-lg flex items-center justify-center gap-2">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Project
            </button>
          </div>
        </div>
      </GlassCard>

      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <GlassCard className={cn("p-5 border-l-4", result.deficitPeriodCount > 0 ? "border-l-red-500" : "border-l-emerald-500")}>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Deficit Periods</div>
              <div className={cn("text-2xl font-bold", result.deficitPeriodCount > 0 ? "text-red-400" : "text-emerald-400")}>
                {result.deficitPeriodCount}
              </div>
            </GlassCard>
            <GlassCard className="p-5 border-l-4 border-l-amber-500">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Max Deficit</div>
              <div className="text-2xl font-bold text-amber-400">{fmtCurrency(result.maxDeficit)}</div>
            </GlassCard>
            <GlassCard className="p-5 border-l-4 border-l-blue-500">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Terminal Balance</div>
              <div className="text-2xl font-bold text-white">{fmtCurrency(result.terminalBalance)}</div>
            </GlassCard>
          </div>

          {/* Cash Flow Timeline */}
          <GlassCard className="p-6">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Quarterly Cash Flows (Annual Summary)</h3>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {/* Group by year */}
              {Array.from(new Set((result.periods || []).map(p => p.year))).slice(0, 30).map(year => {
                const yearPeriods = result.periods.filter(p => p.year === year);
                const yearAsset = yearPeriods.reduce((s, p) => s + p.assetInflow, 0);
                const yearLiab = yearPeriods.reduce((s, p) => s + p.liabilityOutflow, 0);
                const yearNet = yearAsset - yearLiab;
                const lastQ = yearPeriods[yearPeriods.length - 1];
                return (
                  <div key={year} className="flex items-center gap-4 px-3 py-2 rounded-lg hover:bg-slate-900/50 text-sm">
                    <span className="text-slate-500 font-mono w-12">{year}</span>
                    <span className="text-emerald-400 w-28 text-right">{fmtCurrency(yearAsset)}</span>
                    <span className="text-red-400 w-28 text-right">{fmtCurrency(-yearLiab)}</span>
                    <span className={cn("font-bold w-28 text-right", yearNet >= 0 ? "text-emerald-400" : "text-red-400")}>
                      {fmtCurrency(yearNet)}
                    </span>
                    <span className={cn("font-mono w-32 text-right", lastQ?.cumulativeBalance >= 0 ? "text-white" : "text-red-400 font-bold")}>
                      {lastQ ? fmtCurrency(lastQ.cumulativeBalance) : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-8 text-[10px] text-slate-600 px-3">
              <span>Year</span>
              <span className="text-emerald-400/60">Asset Inflows</span>
              <span className="text-red-400/60">Liability Outflows</span>
              <span>Net Cash Flow</span>
              <span>Cumulative Balance</span>
            </div>
          </GlassCard>
        </motion.div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 4: Liability Upload
// ---------------------------------------------------------------------------

const LiabilityUploadTab: React.FC<{ portfolioId: number | null; onUploadComplete: () => void }> = ({ portfolioId, onUploadComplete }) => {
  const [csvText, setCsvText] = useState('');
  const [scenario, setScenario] = useState('BASE');
  const [sourceSystem, setSourceSystem] = useState('AXIS');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!portfolioId || !csvText.trim()) return;
    setUploading(true);
    setUploadResult(null);
    try {
      // Parse CSV: year, quarter, grossOutflow, premiumInflow, netOutflow, discountRate
      const rows = csvText.trim().split('\n').filter(l => !l.startsWith('year') && l.trim()).map(line => {
        const parts = line.split(',').map(s => s.trim());
        return {
          year: parseInt(parts[0]),
          quarter: parseInt(parts[1]),
          grossOutflow: parseFloat(parts[2]) || 0,
          premiumInflow: parseFloat(parts[3]) || 0,
          netOutflow: parseFloat(parts[4]) || parseFloat(parts[2]) - (parseFloat(parts[3]) || 0),
          discountRate: parseFloat(parts[5]) || 0.05
        };
      });

      await axios.post(`/api/insurance/alm/liabilities/${portfolioId}/upload`, rows, {
        params: { scenario, sourceSystem }
      });
      setUploadResult(`Uploaded ${rows.length} cash flow projections for scenario: ${scenario}`);
      setCsvText('');
      onUploadComplete();
    } catch (e) {
      setUploadResult('Upload failed. Check CSV format.');
    }
    setUploading(false);
  };

  return (
    <div className="space-y-6">
      <GlassCard className="p-6">
        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
          <Upload className="w-5 h-5 text-cyan-400" /> Upload Liability Cash Flows
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Paste CSV from AXIS or Prophet: year, quarter, grossOutflow, premiumInflow, netOutflow, discountRate
        </p>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Source System</label>
            <select value={sourceSystem} onChange={e => setSourceSystem(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-900 border border-white/10 rounded-lg text-white text-sm">
              <option value="AXIS">Moody's AXIS</option>
              <option value="PROPHET">FIS Prophet</option>
              <option value="MANUAL">Manual Entry</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Scenario</label>
            <select value={scenario} onChange={e => setScenario(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-900 border border-white/10 rounded-lg text-white text-sm">
              <option value="BASE">Base Case</option>
              <option value="RATES_UP_300">S2 — Rates Up 300bps</option>
              <option value="RATES_DOWN_200">S3 — Rates Down 200bps</option>
              <option value="RATES_UP_GRADED">S4 — Graded Up 300bps</option>
              <option value="RATES_DOWN_GRADED">S5 — Graded Down 200bps</option>
              <option value="POP_UP_200">S6 — Pop Up 200bps</option>
              <option value="POP_DOWN_200">S7 — Pop Down 200bps</option>
              <option value="COMPANY_STRESS">S8 — Company-Specific Stress</option>
            </select>
          </div>
        </div>
        <textarea
          value={csvText}
          onChange={e => setCsvText(e.target.value)}
          placeholder="year,quarter,grossOutflow,premiumInflow,netOutflow,discountRate
2026,1,25000000,0,25000000,0.048
2026,2,25000000,0,25000000,0.048
..."
          className="w-full h-48 px-4 py-3 bg-slate-900 border border-white/10 rounded-lg text-white text-sm font-mono resize-y"
        />
        <div className="flex items-center justify-between mt-4">
          <button onClick={handleUpload} disabled={uploading || !portfolioId || !csvText.trim()}
            className="px-6 py-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold rounded-lg flex items-center gap-2">
            {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Cash Flows
          </button>
          {uploadResult && (
            <span className={cn("text-sm font-semibold", uploadResult.includes('failed') ? "text-red-400" : "text-emerald-400")}>
              {uploadResult}
            </span>
          )}
        </div>
      </GlassCard>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

type TabType = 'summary' | 'rateshock' | 'cashflow' | 'upload';

const InsuranceALMDashboardScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    axios.get('/api/dimensions/portfolios').then(res => {
      const list = res.data.content ?? res.data;
      const arr = Array.isArray(list) ? list.slice(0, 50) : [];
      setPortfolios(arr);
      if (arr.length > 0) setSelectedPortfolio(arr[0].id);
    }).catch(() => {});
  }, []);

  return (
    <Container>
      <TabsContainer>
        <div className="max-w-[1800px] mx-auto mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
                <Target className="w-7 h-7 text-cyan-400" />
                Insurance ALM Dashboard
              </h1>
              <p className="text-sm text-slate-500 mt-1">Asset-Liability Management — duration matching, KRD analysis, cash flow projections</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-bold text-slate-500 uppercase">Portfolio</label>
              <select value={selectedPortfolio ?? ''} onChange={e => setSelectedPortfolio(Number(e.target.value))}
                className="px-4 py-2 bg-slate-900 border border-white/10 rounded-lg text-white text-sm min-w-[200px]">
                {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
              </select>
            </div>
          </div>
        </div>
        <TabsWrapper>
          <ALMTab icon={<Activity className="w-4 h-4" />} label="ALM Summary" active={activeTab === 'summary'} onClick={() => setActiveTab('summary')} />
          <ALMTab icon={<Zap className="w-4 h-4" />} label="Rate Shock" active={activeTab === 'rateshock'} onClick={() => setActiveTab('rateshock')} />
          <ALMTab icon={<BarChart3 className="w-4 h-4" />} label="Cash Flow Projection" active={activeTab === 'cashflow'} onClick={() => setActiveTab('cashflow')} />
          <ALMTab icon={<Upload className="w-4 h-4" />} label="Liability Upload" active={activeTab === 'upload'} onClick={() => setActiveTab('upload')} />
        </TabsWrapper>
      </TabsContainer>

      <ContentContainer>
        <AnimatePresence mode="wait">
          <motion.div key={`${activeTab}-${refreshKey}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
            {activeTab === 'summary' && <ALMSummaryTab portfolioId={selectedPortfolio} />}
            {activeTab === 'rateshock' && <RateShockTab portfolioId={selectedPortfolio} />}
            {activeTab === 'cashflow' && <CashFlowProjectionTab portfolioId={selectedPortfolio} />}
            {activeTab === 'upload' && <LiabilityUploadTab portfolioId={selectedPortfolio} onUploadComplete={() => setRefreshKey(k => k + 1)} />}
          </motion.div>
        </AnimatePresence>
      </ContentContainer>
    </Container>
  );
};

export default InsuranceALMDashboardScreen;
